import React, { useState } from 'react';
import { Cpu, Brain, Sparkles, Info, AlertTriangleIcon, TrendingUp, TrendingDown, ShieldCheck, ShieldAlert, ShieldQuestion, ChevronDown } from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Collapsible "ML reasoning behind score" — hidden by default so the plain-English
// summary leads, with the technical detail one tap away.
export const MLReasoning = ({ children, testId = 'ml-reasoning-toggle' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`${testId}-wrap`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 p-3 sm:p-3.5 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Brain className="w-4 h-4 text-primary" /> ML reasoning behind score
        </span>
        <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
      </button>
      {open && <div className="mt-3" data-testid={`${testId}-content`}>{children}</div>}
    </div>
  );
};

// Plain-English "is it safe & why" card shown ABOVE the technical breakdown.
export const PlainSummary = ({ summary, testId = 'plain-summary' }) => {
  if (!summary) return null;
  const cfg = {
    safe: { icon: ShieldCheck, color: 'text-success', border: 'border-success/40', bg: 'bg-success/5', chip: 'safe' },
    caution: { icon: ShieldQuestion, color: 'text-warning', border: 'border-warning/40', bg: 'bg-warning/5', chip: 'suspicious' },
    danger: { icon: ShieldAlert, color: 'text-destructive', border: 'border-destructive/40', bg: 'bg-destructive/5', chip: 'danger' },
  }[summary.level] || { icon: Info, color: 'text-muted-foreground', border: 'border-border', bg: 'bg-muted/30', chip: 'unknown' };
  const Icon = cfg.icon;
  return (
    <GlassCard className={cn('p-4 sm:p-5 border-2', cfg.border, cfg.bg)} data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', cfg.bg)}>
          <Icon className={cn('w-5 h-5', cfg.color)} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={cn('font-display text-base sm:text-lg font-bold', cfg.color)}>{summary.headline}</h3>
          </div>
          {summary.lines.map((line, i) => (
            <p key={i} className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-1">{line}</p>
          ))}
          <p className="text-[10px] text-muted-foreground/70 mt-1.5 flex items-center gap-1">
            <Info className="w-3 h-3" /> Plain-English summary — full technical breakdown below.
          </p>
        </div>
      </div>
    </GlassCard>
  );
};


// Small badge telling the user whether the score came from the trained model or
// the deterministic heuristic fallback.
export const MLSourceBadge = ({ ml }) => {
  if (!ml || ml.available === false || ml.source !== 'model') {
    return (
      <Badge variant="suspicious" className="text-[10px]" data-testid="ml-source-badge">
        <Cpu className="w-3 h-3 mr-1" /> Heuristic fallback
      </Badge>
    );
  }
  return (
    <Badge variant="safe" className="text-[10px]" data-testid="ml-source-badge">
      <Brain className="w-3 h-3 mr-1" /> ML model
    </Badge>
  );
};

const SectionHeader = ({ ml, subtitle }) => (
  <div className="flex items-center justify-between mb-3">
    <h4 className="text-sm font-semibold flex items-center gap-2">
      <Sparkles className="w-4 h-4 text-primary" /> {subtitle}
    </h4>
    <MLSourceBadge ml={ml} />
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-muted/50" data-testid={testId}>
    <span className="text-[11px] sm:text-xs text-muted-foreground">{label}</span>
    <span className="text-[11px] sm:text-xs font-mono font-medium">{value}</span>
  </div>
);

const ProbBar = ({ prob, dangerLabel }) => {
  if (prob === null || prob === undefined) return null;
  const pct = Math.round(prob * 100);
  return (
    <div className="mb-3">
      <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
        <span>{dangerLabel}</span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className={cn('h-full rounded-full transition-all duration-500',
          pct >= 60 ? 'bg-destructive' : pct >= 30 ? 'bg-warning' : 'bg-success')}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ── LINK ────────────────────────────────────────────────────────────
export const MLLinkInsights = ({ ml }) => {
  if (!ml) return null;
  const f = ml.features || {};
  return (
    <GlassCard className="p-4 sm:p-5 border-primary/20 border" data-testid="ml-link-insights">
      <SectionHeader ml={ml} subtitle="ML Signal Breakdown" />
      {ml.available === false ? (
        <p className="text-xs text-muted-foreground">Model service unavailable — showing deterministic heuristic score.</p>
      ) : (
        <>
          <ProbBar prob={ml.probMalicious} dangerLabel="Model: probability malicious" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Stat label="Registered domain" value={f.registered_domain || '—'} />
            <Stat label="TLD risk" value={f.tld_risk ? 'High' : 'Normal'} />
            <Stat label="Domain entropy" value={f.registered_entropy} />
            <Stat label="Host length" value={f.hostname ? f.hostname.length : '—'} />
            <Stat label="Digit ratio (host)" value={f.digit_ratio_host} />
            <Stat label="Subdomain count" value={f.subdomain_count} />
            <Stat label="Special-char density" value={f.special_char_density} />
            <Stat label="Sensitive keyword" value={f.has_sensitive_word ? 'Yes' : 'No'} />
          </div>
          {ml.hardCapApplied && (
            <p className="text-[11px] text-destructive mt-2 flex items-center gap-1">
              <AlertTriangleIcon className="w-3 h-3" /> Hard red flag detected — score capped by protection rules.
            </p>
          )}
        </>
      )}
    </GlassCard>
  );
};

const TermChips = ({ terms, filter, colorClass }) => {
  const list = (terms || []).filter((t) => (filter === 'malicious' ? t.weight > 0 : t.weight < 0)).slice(0, 6);
  if (!list.length) return <span className="text-[11px] text-muted-foreground">None</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((t, i) => (
        <span key={i} className={cn('px-2 py-1 rounded-md text-[11px] font-mono', colorClass)}>
          {t.term} <span className="opacity-70">({t.weight > 0 ? '+' : ''}{t.weight})</span>
        </span>
      ))}
    </div>
  );
};

// ── EMAIL ───────────────────────────────────────────────────────────
export const MLEmailInsights = ({ ml }) => {
  if (!ml) return null;
  const struct = ml.structuralFeatures || {};
  return (
    <GlassCard className="p-4 sm:p-5 border-primary/20 border" data-testid="ml-email-insights">
      <SectionHeader ml={ml} subtitle="ML Signal Breakdown" />
      {ml.available === false ? (
        <p className="text-xs text-muted-foreground">Model service unavailable — showing deterministic heuristic score.</p>
      ) : (
        <>
          <ProbBar prob={ml.probPhishing} dangerLabel="Model: probability phishing" />
          {ml.hasContent ? (
            <div className="mb-3">
              <p className="text-[11px] text-muted-foreground mb-1.5">Top contributing terms (TF-IDF × coefficient)</p>
              <TermChips terms={ml.topTerms} filter="malicious" colorClass="bg-destructive/10 text-destructive" />
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground mb-3">Add the email body to run the text model.</p>
          )}
          <p className="text-[11px] text-muted-foreground mb-1.5">Structural signals (computed; see note)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(struct).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-muted/50" data-testid={`ml-struct-${k}`}>
                <span className="text-[11px] text-muted-foreground">{k.replace(/_/g, ' ')}</span>
                <span className="text-[11px] font-mono flex items-center gap-1.5">
                  {v.available ? v.value : <span className="italic text-muted-foreground/70">unavailable</span>}
                  {v.contributedToScore && <Badge variant="safe" className="text-[9px] py-0">used</Badge>}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground/80 mt-2 leading-relaxed">{ml.structuralNote}</p>
        </>
      )}
    </GlassCard>
  );
};

// ── ARTICLE ─────────────────────────────────────────────────────────
export const MLArticleInsights = ({ ml }) => {
  if (!ml) return null;
  return (
    <GlassCard className="p-4 sm:p-5 border-primary/20 border" data-testid="ml-article-insights">
      <SectionHeader ml={ml} subtitle="ML Signal Breakdown" />
      {ml.available === false ? (
        <p className="text-xs text-muted-foreground">Model service unavailable — showing deterministic heuristic score.</p>
      ) : (
        <>
          <ProbBar prob={ml.probFake} dangerLabel="Model: probability fake/misleading" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <p className="text-[11px] text-destructive mb-1.5 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Pushed toward fake
              </p>
              <TermChips terms={ml.topTerms} filter="malicious" colorClass="bg-destructive/10 text-destructive" />
            </div>
            <div>
              <p className="text-[11px] text-success mb-1.5 flex items-center gap-1">
                <TrendingDown className="w-3 h-3" /> Pushed toward real
              </p>
              <TermChips terms={ml.topTerms} filter="legitimate" colorClass="bg-success/10 text-success" />
            </div>
          </div>
          <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
            <p className="text-[11px] text-foreground/80 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 text-warning flex-shrink-0 mt-0.5" />
              <span>{ml.caveat}</span>
            </p>
          </div>
        </>
      )}
    </GlassCard>
  );
};

// ── FULL FEATURE TABLE (Phishing Lab) ───────────────────────────────
export const MLFeatureTable = ({ title, data }) => (
  <GlassCard className="p-4 sm:p-5" data-testid="ml-feature-table">
    <h4 className="text-sm font-semibold mb-3">{title}</h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
      {Object.entries(data).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-muted/40">
          <span className="text-[11px] text-muted-foreground">{k.replace(/_/g, ' ')}</span>
          <span className="text-[11px] font-mono font-medium truncate max-w-[45%] text-right">
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  </GlassCard>
);
